
  import {
    defineComponent,
    computed,
    ref,
    watch,
    onMounted,
    onUnmounted,
  } from 'vue';
  import ExpertReviewDrawer from './ExpertReviewDrawer.vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import {
    ExpertReviewAreaDetailsObject,
    ExpertReviewAreaObject,
    ExpertReviewFilterKey,
    ExpertReviewQuarterStatus,
  } from '@/store/modules/Indices/ExpertReviewModule';
  import {
    FILTERS,
    deltaClass,
    fmt,
    livePrice,
    liveQoq,
    pct,
    useExpertReviewI18n,
  } from '@/core/helpers/expert-review';

  const TOAST_MS = 2600;
  const DRAWER_EXIT_MS = 300;
  const BADGES = {
    OBSERVED: 'badge-light',
    MIXED: 'badge-light-warning',
    CLUSTER_CARRIED: 'badge-light-danger',
  };

  export default defineComponent({
    name: 'expert-review-listing',
    async setup() {
      const store = useStore();
      const route = useRoute();
      const router = useRouter();
      const { can } = useAbility();
      const { translate, isArabic } = useExpertReviewI18n();

      const filter = computed<ExpertReviewFilterKey>(
        () => store.getters.expertReviewFilter
      );
      const quarterStatus = computed<ExpertReviewQuarterStatus>(
        () => store.getters.expertReviewQuarterStatus
      );
      const cursor = ref(0);
      const loading = ref(false);
      const escalationHours = ref<number | null>(null);
      const savingEscalation = ref(false);
      const maxEscalationHours = 720;

      const loadEscalationHours = async () => {
        try {
          const levels = await store.dispatch(
            Actions.GET_PPM_NOTIFICATION_LEVELS
          );
          escalationHours.value =
            levels?.find((level) => level.escalationHours != null)
              ?.escalationHours ?? null;
        } catch {
          escalationHours.value = null;
        }
      };

      const saveEscalationHours = async () => {
        if (savingEscalation.value || !escalationHours.value) return;

        savingEscalation.value = true;
        try {
          await store.dispatch(Actions.UPDATE_PPM_ESCALATION_HOURS, {
            escalationHours: escalationHours.value,
          });
          showToast(
            String(escalationHours.value),
            translate('expertReviewEscalationSavedToast')
          );
        } catch {
          showRequestError('expertReviewEscalationSaveFailed');
        } finally {
          savingEscalation.value = false;
        }
      };

      const toast = ref({ visible: false, subject: '', message: '' });
      const selectedId = ref<number | null>(null);
      const drawerOpen = ref(false);

      let toastTimer = 0;
      let drawerTimer = 0;

      const quarter = computed(() => store.getters.expertReviewQuarter);
      const isFinal = computed(() => quarter.value.isFinal);
      const elapsed = computed(() =>
        isFinal.value ? 100 : quarter.value.elapsedPercentage
      );

      const rows = computed<Array<ExpertReviewAreaObject>>(
        () => store.getters.expertReviewAreasList
      );

      const counts = computed(() => store.getters.expertReviewCounts);
      const rejectReasons = computed(
        () => store.getters.expertReviewRejectReasons
      );
      const detailsStatus = computed(
        () => store.getters.expertReviewDetailsStatus
      );
      const reasonsStatus = computed(
        () => store.getters.expertReviewReasonsStatus
      );
      const totalItems = computed(() => store.getters.expertReviewTotalItems);
      const currentPage = computed(() => store.getters.expertReviewCurrentPage);
      const pageSize = computed(() => store.getters.expertReviewPageSize);

      const reviewedCount = computed(
        () => counts.value.all - counts.value.PENDING
      );
      const reviewedPercentage = computed(() =>
        counts.value.all ? (reviewedCount.value / counts.value.all) * 100 : 0
      );
      const quarterLabel = computed(() =>
        translate(
          isFinal.value
            ? 'expertReviewQuarterLabelFinal'
            : 'expertReviewQuarterLabelProvisional',
          { quarter: quarter.value.code }
        )
      );
      const details = computed<ExpertReviewAreaDetailsObject>(
        () => store.getters.expertReviewAreaDetails
      );

      const selectedRow = computed(() => {
        const row =
          rows.value.find((item) => item.id === selectedId.value) ??
          (details.value.area?.id === selectedId.value
            ? details.value.area
            : null);

        return row
          ? {
              ...row,
              priceHistory: details.value.priceHistory,
              deals: details.value.deals,
            }
          : null;
      });
      const selectedClusterDeals = computed(() => details.value.clusterDeals);

      const openDrawer = (id: number) => {
        const index = rows.value.findIndex((item) => item.id === id);
        if (index !== -1) cursor.value = index;
        window.clearTimeout(drawerTimer);
        selectedId.value = id;
        drawerOpen.value = true;
        if (Number(route.params.id) !== id) {
          router.push({
            name: 'expert-review-listing',
            params: { id: String(id) },
          });
        }
        store.dispatch(Actions.GET_EXPERT_REVIEW_AREA_DETAILS, id);
        if (reasonsStatus.value === 'error') {
          store.dispatch(Actions.GET_EXPERT_REVIEW_REJECT_REASONS);
        }
      };

      const closeDrawer = () => {
        drawerOpen.value = false;
        window.clearTimeout(drawerTimer);
        drawerTimer = window.setTimeout(() => {
          selectedId.value = null;
        }, DRAWER_EXIT_MS);
        if (route.params.id) {
          router.push({ name: 'expert-review-listing', params: {} });
        }
      };

      watch(
        () => route.params.id,
        (id) => {
          const next = Number(id);
          if (next && next !== selectedId.value) openDrawer(next);
          if (!next && drawerOpen.value) closeDrawer();
        }
      );

      const setQuarterStatus = async (next: ExpertReviewQuarterStatus) => {
        if (loading.value || quarterStatus.value === next) return;

        loading.value = true;
        cursor.value = 0;
        try {
          await store.dispatch(
            Actions.UPDATE_EXPERT_REVIEW_QUARTER_STATUS,
            next
          );
        } catch {
          showRequestError();
        } finally {
          loading.value = false;
        }
      };

      const setFilter = async (key: ExpertReviewFilterKey) => {
        if (loading.value || filter.value === key) return;

        loading.value = true;
        cursor.value = 0;
        try {
          await store.dispatch(Actions.UPDATE_EXPERT_REVIEW_FILTER, key);
        } catch {
          showRequestError();
        } finally {
          loading.value = false;
        }
      };

      const pageChanged = async (page: number) => {
        if (loading.value) return;

        loading.value = true;
        cursor.value = 0;
        try {
          await store.dispatch(Actions.UPDATE_EXPERT_REVIEW_PAGE, page);
        } catch {
          showRequestError();
        } finally {
          loading.value = false;
        }
      };

      watch(rows, (list) => {
        cursor.value = Math.min(cursor.value, Math.max(0, list.length - 1));
      });

      /* ---------- decisions ---------- */
      const showToast = (subject: string, message: string) => {
        toast.value = { visible: true, subject, message };
        window.clearTimeout(toastTimer);
        toastTimer = window.setTimeout(() => {
          toast.value = { ...toast.value, visible: false };
        }, TOAST_MS);
      };

      const showRequestError = (fallbackKey = 'expertReviewLoadFailed') => {
        const [name] = Object.keys(store.getters.getErrors || {});
        const message = name ? store.getters.getErrors[name]?.[0] : '';
        Swal.fire({
          text: translate(message || fallbackKey),
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: translate('tryAgainExcl'),
          customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
        });
      };
      const showDecisionError = () =>
        showRequestError('expertReviewDecisionFailed');

      const approve = async (
        row: ExpertReviewAreaObject,
        expertValue: number | null = null
      ) => {
        if (loading.value || !can('update', 'indices')) return false;

        const confirmed = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate('expertReviewConfirmApprove', {
            area: isArabic.value ? row.titleAr : row.titleEn,
            value: fmt(expertValue ?? row.pricePerSqm),
          }),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (!confirmed.value) return false;

        loading.value = true;
        let published = false;
        try {
          await store.dispatch(Actions.APPROVE_EXPERT_REVIEW_AREAS, {
            ids: [row.id],
            expertValue,
          });
          published = true;
          await store.dispatch(Actions.GET_EXPERT_REVIEW_AREAS);
        } catch {
          if (!published) {
            showDecisionError();
            return false;
          }
          showRequestError('expertReviewListStale');
        } finally {
          loading.value = false;
        }

        showToast(
          isArabic.value ? row.titleAr : row.titleEn,
          translate('expertReviewApprovedToast', {
            value: fmt(expertValue ?? row.pricePerSqm),
          })
        );
        return true;
      };

      const approveLowRisk = async () => {
        if (loading.value || !can('update', 'indices')) return;

        const reviewedQuarter = quarter.value.code;

        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate('expertReviewConfirmBulkApprove', {
            quarter: reviewedQuarter,
          }),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (!result.value) return;

        loading.value = true;
        let published = false;
        let decided = 0;
        try {
          const decision = await store.dispatch(
            Actions.APPROVE_LOW_RISK_EXPERT_REVIEW_AREAS,
            {
              quarterStatus: quarterStatus.value,
              quarterCode: reviewedQuarter,
            }
          );
          decided = decision?.decided?.length ?? 0;
          published = true;
          await store.dispatch(Actions.GET_EXPERT_REVIEW_AREAS);
        } catch {
          if (!published) {
            showDecisionError();
            return;
          }
          showRequestError('expertReviewListStale');
        } finally {
          loading.value = false;
        }

        showToast(String(decided), translate('expertReviewBulkApprovedToast'));
      };

      const approveFromDrawer = async (expertValue: number | null) => {
        const row = selectedRow.value;
        if (!row) return;
        if (await approve(row, expertValue)) closeDrawer();
      };

      const rejectFromDrawer = async ({
        rejectReasonId,
        rejectReasonNote,
        expertValue,
      }: {
        rejectReasonId: number;
        rejectReasonNote?: string;
        expertValue: number | null;
      }) => {
        const row = selectedRow.value;
        if (loading.value || !row || !can('update', 'indices')) return;

        loading.value = true;
        let published = false;
        try {
          await store.dispatch(Actions.REJECT_EXPERT_REVIEW_AREA, {
            id: row.id,
            rejectReasonId,
            rejectReasonNote,
            expertValue,
          });
          published = true;
          await store.dispatch(Actions.GET_EXPERT_REVIEW_AREAS);
        } catch {
          if (!published) {
            showDecisionError();
            return;
          }
          showRequestError('expertReviewListStale');
        } finally {
          loading.value = false;
        }

        showToast(
          isArabic.value ? row.titleAr : row.titleEn,
          translate(
            expertValue !== null
              ? 'expertReviewRejectedAdjustedToast'
              : 'expertReviewRejectedProvisionalToast',
            {
              value: fmt(
                expertValue !== null ? expertValue : row.previousPricePerSqm
              ),
            }
          )
        );
        closeDrawer();
      };

      const decisionLabel = (row: ExpertReviewAreaObject) =>
        translate(
          row.status === 'REJECTED'
            ? 'expertReviewRejected'
            : row.status === 'ADJUSTED'
            ? 'expertReviewAdjusted'
            : 'expertReviewApproved'
        ) + (row.expertValue !== null ? ` · ${fmt(row.expertValue)}` : '');
      const decisionClass = (row: ExpertReviewAreaObject) =>
        row.status === 'REJECTED'
          ? 'text-danger'
          : row.status === 'ADJUSTED'
          ? 'text-warning'
          : 'text-success';
      const decisionSeal = (row: ExpertReviewAreaObject) =>
        row.status === 'REJECTED' ? '✕' : row.status === 'ADJUSTED' ? '±' : '✓';

      /* ---------- keyboard ---------- */
      const onKeydown = (event: KeyboardEvent) => {
        const tag = (event.target as HTMLElement)?.tagName || '';
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

        const key = (event.key || '').toLowerCase();

        if (drawerOpen.value) {
          if (key === 'escape') closeDrawer();
          return;
        }

        const list = rows.value;
        if (!list.length) return;
        const current = list[Math.min(cursor.value, list.length - 1)];

        if (key === 'j' || key === 'arrowdown') {
          cursor.value = Math.min(cursor.value + 1, list.length - 1);
          event.preventDefault();
        } else if (key === 'k' || key === 'arrowup') {
          cursor.value = Math.max(cursor.value - 1, 0);
          event.preventDefault();
        } else if (
          key === 'a' &&
          current.status === 'PENDING' &&
          can('update', 'indices')
        ) {
          approve(current);
        } else if (
          key === 'enter' ||
          (key === 'r' &&
            current.status === 'PENDING' &&
            can('update', 'indices'))
        ) {
          openDrawer(current.id);
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('expertReviewListing'), [
          translate('indices'),
        ]);
        document.addEventListener('keydown', onKeydown);
        loadEscalationHours();
      });

      onUnmounted(() => {
        document.removeEventListener('keydown', onKeydown);
        window.clearTimeout(toastTimer);
        window.clearTimeout(drawerTimer);
      });

      await store.dispatch(Actions.GET_EXPERT_REVIEW_AREAS);
      store.dispatch(Actions.GET_EXPERT_REVIEW_REJECT_REASONS);
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      if (Number(route.params.id)) openDrawer(Number(route.params.id));

      return {
        can,
        translate,
        isArabic,
        BADGES,
        loading,
        rows,
        quarter,
        isFinal,
        elapsed,
        quarterLabel,
        filters: FILTERS,
        filter,
        setFilter,
        quarterStatus,
        setQuarterStatus,
        counts,
        rejectReasons,
        detailsStatus,
        reasonsStatus,
        totalItems,
        currentPage,
        pageSize,
        pageChanged,
        fmt,
        livePrice,
        liveQoq,
        pct,
        deltaClass,
        approveLowRisk,
        escalationHours,
        savingEscalation,
        maxEscalationHours,
        saveEscalationHours,
        decisionLabel,
        decisionClass,
        decisionSeal,
        approve,
        approveFromDrawer,
        rejectFromDrawer,
        toast,
        cursor,
        selectedRow,
        selectedClusterDeals,
        drawerOpen,
        openDrawer,
        closeDrawer,
        reviewedCount,
        reviewedPercentage,
      };
    },
    components: { ExpertReviewDrawer },
  });
